import { useState } from 'react';
import { createPortal } from 'react-dom';
import { differenceInDays } from 'date-fns';
import type { WaitlistEntry, PendingBackfill } from './waitlistTypes';

// ─── Acuity color map ──────────────────────────────────────────────────────

const ACUITY_STYLE: Record<string, { bg: string; border: string; color: string }> = {
  urgent:  { bg: '#fef2f2', border: '#fca5a5', color: '#dc2626' },
  high:    { bg: '#fffbeb', border: '#fcd34d', color: '#d97706' },
  routine: { bg: '#eff6ff', border: '#93c5fd', color: '#2563eb' },
};

// ─── Tiny icons ────────────────────────────────────────────────────────────

function ProviderPrefIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

// ─── WaitlistEntryRow ──────────────────────────────────────────────────────

interface WaitlistEntryRowProps {
  entry: WaitlistEntry;
  pendingBackfill: PendingBackfill | undefined;
  highlighted: boolean;
  onHighlight: (id: string | null) => void;
}

export function WaitlistEntryRow({ entry, pendingBackfill, highlighted, onHighlight }: WaitlistEntryRowProps) {
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const acuity = ACUITY_STYLE[entry.acuityLevel] ?? ACUITY_STYLE.routine;
  const waitDays = differenceInDays(new Date(), new Date(entry.waitingSince));

  const expiresWarning = (() => {
    if (!entry.expiresAt) return null;
    const hoursLeft = (new Date(entry.expiresAt).getTime() - Date.now()) / 3_600_000;
    if (hoursLeft > 48) return null;
    return hoursLeft <= 0 ? 'Expired' : `Expires in ${Math.ceil(hoursLeft / 24)}d`;
  })();

  return (
    <div
      onClick={() => onHighlight(highlighted ? null : entry.id)}
      style={{
        padding: '8px 10px',
        borderBottom: '1px solid #f1f5f9',
        background: highlighted ? '#eff6ff' : '#fff',
        cursor: 'pointer',
        transition: 'background 0.1s',
        borderLeft: highlighted ? '3px solid #3b82f6' : '3px solid transparent',
      }}
      onMouseEnter={(e) => { if (!highlighted) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { if (!highlighted) (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
    >
      {/* Row 1: priority badge + label + wait duration */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span
          style={{
            fontSize: 9,
            fontWeight: 700,
            padding: '1px 5px',
            borderRadius: 3,
            background: acuity.bg,
            color: acuity.color,
            border: `1px solid ${acuity.border}`,
            flexShrink: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          #{entry.priority}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.displayLabel}
        </span>
        <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0 }}>
          {waitDays}d
        </span>
      </div>

      {/* Row 2: preference icons + in-flight indicator + expiry warning */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {/* Acuity chip */}
        <span style={{ fontSize: 9, color: acuity.color, fontWeight: 600, letterSpacing: '0.02em' }}>
          {entry.acuityLevel.toUpperCase()}
        </span>

        {/* Preference icons */}
        {(entry.preferences.resourceIds?.length ?? 0) > 0 && (
          <span title="Provider preference" style={{ color: '#64748b', display: 'flex' }}>
            <ProviderPrefIcon />
          </span>
        )}
        {entry.preferences.timeOfDay && entry.preferences.timeOfDay !== 'any' && (
          <span title={`${entry.preferences.timeOfDay} slot preferred`} style={{ color: '#64748b', display: 'flex' }}>
            <ClockIcon />
          </span>
        )}
        {entry.preferences.locationPreference === 'telehealth' && (
          <span title="Telehealth preferred" style={{ color: '#64748b', display: 'flex' }}>
            <VideoIcon />
          </span>
        )}

        {/* In-flight offer indicator */}
        {pendingBackfill && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 9,
              color: '#d97706',
              fontWeight: 600,
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: 3,
              padding: '1px 4px',
              animation: 'fse-backfill-pulse 1.5s ease-in-out infinite',
            }}
          >
            <SendIcon />
            Offer sent
          </span>
        )}

        {/* Expiry warning */}
        {expiresWarning && (
          <span
            style={{
              fontSize: 9,
              color: '#b45309',
              fontWeight: 600,
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: 3,
              padding: '1px 4px',
            }}
          >
            {expiresWarning}
          </span>
        )}

        {/* Notes tooltip trigger */}
        {entry.notes && (
          <span
            onMouseEnter={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setTooltipPos(null)}
            style={{ color: '#94a3b8', cursor: 'help', fontSize: 9, fontStyle: 'italic' }}
          >
            note
          </span>
        )}
      </div>

      {/* Notes portal tooltip */}
      {entry.notes && tooltipPos && createPortal(
        <div style={{
          position: 'fixed',
          left: tooltipPos.x + 12,
          top: tooltipPos.y + 12,
          background: '#1f2937',
          color: '#fff',
          padding: '5px 8px',
          borderRadius: 4,
          fontSize: 11,
          lineHeight: 1.4,
          maxWidth: 240,
          zIndex: 99999,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          wordBreak: 'break-word',
        }}>
          {entry.notes}
        </div>,
        document.body,
      )}
    </div>
  );
}
